import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SurveyPermissionsPage() {
  const { getAuthHeader, employee } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const allowed = ['Super Admin', 'Admin Head'].includes(employee?.role);

  useEffect(() => {
    if (!allowed) return;
    (async () => {
      const r = await fetch(`${API}/surveys/permissions`, { headers: getAuthHeader() });
      if (r.ok) setRows(await r.json());
    })();
  }, [getAuthHeader, allowed]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r => (r.full_name || '').toLowerCase().includes(s) || (r.department || '').toLowerCase().includes(s) || (r.email || '').toLowerCase().includes(s));
  }, [rows, q]);

  const toggle = async (row, value) => {
    const r = await fetch(`${API}/surveys/permissions`, {
      method: 'PUT',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ employee_id: row.employee_id, can_create_surveys: value }),
    });
    if (r.ok) {
      setRows(prev => prev.map(p => p.employee_id === row.employee_id ? { ...p, can_create_surveys: value } : p));
      toast.success(`${value ? 'Granted' : 'Revoked'} for ${row.full_name}`);
    } else {
      toast.error('Update failed');
    }
  };

  if (!allowed) return <Layout><Card><CardContent className="py-10 text-center">Access denied — Admin Head only.</CardContent></Card></Layout>;

  const grantedCount = rows.filter(r => r.can_create_surveys).length;

  return (
    <Layout>
      <div className="space-y-4 max-w-5xl mx-auto" data-testid="survey-perms-page">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Survey Creation Permissions</h1>
          <p className="text-muted-foreground text-sm">Grant "Create Survey" access to any employee across departments. Currently granted to <b>{grantedCount}</b> employees.</p>
        </div>
        <Card>
          <CardContent className="pt-4 space-y-3">
            <Input placeholder="Search by name / email / department" value={q} onChange={e => setQ(e.target.value)} data-testid="perm-search" className="max-w-sm" />
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Can Create Surveys</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(row => (
                    <TableRow key={row.employee_id} data-testid={`perm-row-${row.employee_id}`}>
                      <TableCell className="font-medium">{row.full_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.email}</TableCell>
                      <TableCell>{row.department || '—'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{row.role}</Badge></TableCell>
                      <TableCell>{row.location || '—'}</TableCell>
                      <TableCell className="text-right">
                        <Switch checked={!!row.can_create_surveys} onCheckedChange={(v) => toggle(row, v)} data-testid={`perm-toggle-${row.employee_id}`} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
